import { FC, HTMLAttributes, ReactNode, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { Layout } from 'antd';
import cn from 'classnames';

import { AppDispatch } from 'app/appStore';
import { JoinBrand, JoinBrandModal, JoinInviteBrand } from 'entities/Brand';
import { JoinInviteOffer, JoinOffer, OfferDetailDrawer } from 'entities/Offer';
import { EFilters } from 'features/Filters';
import { Onboarding, openOnboardingModal } from 'features/Onboarding';
import { TelegramModal } from 'features/Telegram';
import { useGetAppConfigQuery, useGetMetaQuery } from 'shared/api/commonApi';
import { EPopups, EStatuses, EYmEvents, EYmEventsO1 } from 'shared/typings';
import { OverlayLoader } from 'shared/ui';
import { sendYmEvent } from 'shared/utils';
import { Alerts } from 'widgets/Alerts';
import { Footer } from 'widgets/Footer';
import { Header } from 'widgets/Header';
import { Sidebar } from 'widgets/Sidebar';

import styles from './MainLayout.module.scss';

interface IMainLayoutProps extends HTMLAttributes<HTMLDivElement> {
	outlet?: ReactNode;
}

export const MainLayout: FC<IMainLayoutProps> = ({ className, outlet, ...props }) => {
	const { data: appConfig, isFetching } = useGetAppConfigQuery();
	const [searchParams, setSearchParams] = useSearchParams();

	const dispatch = useDispatch<AppDispatch>();
	useGetMetaQuery();

	const { pathname } = useLocation();

	useEffect(() => {
		if (appConfig?.user?.user_id) {
			sendYmEvent({ event: EYmEvents.PAGE_VIEW, o1: EYmEventsO1.ACCOUNT });
		}
	}, [pathname, appConfig?.user?.user_id]);

	useEffect(() => {
		if (!appConfig?.user) {
			return;
		}
		if (appConfig.user?.not_shown_popups.includes(EPopups.ONBOARDING)) {
			dispatch(openOnboardingModal());
		}
	}, [appConfig, dispatch]);

	useEffect(() => {
		const filters = JSON.parse(localStorage.getItem(`filters_${appConfig?.user?.user_id}`) || '{}');
		if (
			(!searchParams.toString() ||
				(searchParams.size === 1 && searchParams.get(EFilters.STATUS) === EStatuses.JOINED)) &&
			filters[pathname]
		) {
			setSearchParams(filters[pathname]);
		}
	}, [pathname, appConfig?.user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Layout {...props} className={cn(className, styles.main)} style={{ minHeight: '100vh' }}>
			{(isFetching || !appConfig?.user) && <OverlayLoader opacity={1} zIndex={101} />}
			<Sidebar />
			<Layout>
				<Header />
				<Layout.Content className={styles.content}>{outlet || <Outlet />}</Layout.Content>
				<Footer />
			</Layout>
			<Alerts />
			<Onboarding />
			<OfferDetailDrawer />
			<JoinOffer />
			<JoinInviteOffer />
			<JoinBrand />
			<JoinInviteBrand />
			<JoinBrandModal />
			<TelegramModal />
		</Layout>
	);
};
